import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormLabel, OutlinedInput, Button, Grid, FormHelperText, Select, MenuItem, CircularProgress } from "@mui/material";
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useEffect } from "react";
import { postPatientInformationRequest, resetPostPatientInformation } from "../../../../redux/modules/admin/patientInformation/slice";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { GENDER, ETHNICITY_OPTIONS } from "../../../../constants/Constant";
import moment from "moment";

const validationSchema = yup.object().shape({
    fName: yup.string().required('First Name is required'),
    lName: yup.string().required('Last Name is required'),
    email: yup.string().email('Invalid email').nullable(),
    dob: yup.string().required('Date of Birth is required'),
    gender: yup.string().required('Gender is required'),
});

const AddPatientDialog = ({ open, onClose, onPatientCreated }) => {
    const dispatch = useDispatch();
    let { postRes, loading } = useSelector(state => state.patientInformation);
    let { currentBranch } = useSelector(state => state.branch);
    const { register, handleSubmit, formState: { errors }, getValues, setValue, trigger } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            dob: moment(new Date()).format('YYYY-MM-DD'),
            gender: GENDER.MALE,
        },
    });

    useEffect(() => {
        if (!postRes) return;
        toast(postRes?.message);
        if (postRes?.success) {
            onPatientCreated(postRes.patientdata);
            onClose();
        }
        dispatch(resetPostPatientInformation());
    }, [dispatch, postRes, onPatientCreated, onClose]);

    const onSubmit = (data) => {
        let fileFormData = new FormData();
        dispatch(postPatientInformationRequest({ fileFormData, formData: data, branchId: currentBranch?.id }));
    };

    return (
        <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
            <DialogTitle>Quick Register Patient</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth error={Boolean(errors.fName)}>
                            <FormLabel>First Name *</FormLabel>
                            <OutlinedInput className="inputfield" size="small" placeholder="First Name" {...register('fName')} />
                            {errors?.fName?.message && <FormHelperText>{errors.fName.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth error={Boolean(errors.lName)}>
                            <FormLabel>Last Name *</FormLabel>
                            <OutlinedInput className="inputfield" size="small" placeholder="Last Name" {...register('lName')} />
                            {errors?.lName?.message && <FormHelperText>{errors.lName.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth error={Boolean(errors.email)}>
                            <FormLabel>Email</FormLabel>
                            <OutlinedInput className="inputfield" size="small" placeholder="Email (optional)" {...register('email')} />
                            {errors?.email?.message && <FormHelperText>{errors.email.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth error={Boolean(errors.dob)}>
                            <FormLabel>Date of Birth *</FormLabel>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker className="inputfield" disableFuture format="DD-MM-YYYY" value={moment(getValues().dob)} onChange={(date) => { setValue("dob", moment(date).format('YYYY-MM-DD')); trigger("dob"); }} slotProps={{ textField: { size: 'small' } }} />
                            </LocalizationProvider>
                            {errors?.dob?.message && <FormHelperText>{errors.dob.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth error={Boolean(errors.gender)}>
                            <FormLabel>Gender *</FormLabel>
                            <Select {...register('gender')} size="small" defaultValue={GENDER.MALE} className="inputfield">
                                <MenuItem value={GENDER.MALE}>Male</MenuItem>
                                <MenuItem value={GENDER.FEMALE}>Female</MenuItem>
                                <MenuItem value={GENDER.OTHER}>Other</MenuItem>
                            </Select>
                            {errors?.gender?.message && <FormHelperText>{errors.gender.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Contact Number</FormLabel>
                            <OutlinedInput className="inputfield" size="small" placeholder="Phone number" {...register('contactNumber')} />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <FormControl fullWidth>
                            <FormLabel>Address</FormLabel>
                            <OutlinedInput className="inputfield" size="small" placeholder="Street, city, region" {...register('address')} />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>National ID / Passport No.</FormLabel>
                            <OutlinedInput className="inputfield" size="small" placeholder="National ID or Passport No. (optional)" {...register('nationalId')} />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Ethnicity</FormLabel>
                            <Select {...register('ethnicity')} size="small" defaultValue="" displayEmpty className="inputfield">
                                <MenuItem value=""><em>Not specified</em></MenuItem>
                                {ETHNICITY_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : 'Register & Select'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddPatientDialog;
